import type { PitchFrame } from '@tunasong/music-lib'

/**
 * One capture window: audition, count-in, mute, listen, hand back the frames.
 *
 * Every side effect is injected, so the ordering rules — the two that a broken tool would break
 * silently — are unit-testable without an audio graph:
 *
 *  1. **Playback is muted before a single frame is collected** (§6). With speakers, an unmuted
 *     reference bleeds into the capture and is scored as though the user had sung it. A silent
 *     user would then be handed a perfect score, which for a tool whose entire output is a
 *     measurement is the most dangerous thing it could do.
 *  2. **The window opens with a lead-in the user is not yet cued for** (§5.1). The voicing gate
 *     needs the first 500 ms as room tone to measure the noise floor from. Cue the singer inside
 *     that window and the floor rises to the level of their voice, after which nothing they sing
 *     clears it.
 *
 * The count-in and the reference both sound *before* the mute: they are the cue, and muting them
 * would leave the singer with nothing to sing against. They sound in that order — see
 * `runCaptureWindow`.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1, §6
 */

export type CapturePhase = 'count-in' | 'reference' | 'lead-in' | 'listening' | 'scoring'

export interface CaptureWindowDeps {
  /** Mute every path that reaches the speakers. Called `true` before capture, `false` after. */
  setMuted: (muted: boolean) => void
  /** Subscribe to pitch frames. Resolves to an unsubscribe. */
  startFrames: (onFrame: (frame: PitchFrame) => void) => Promise<() => void>
  /** Audition the reference. Resolves when it has finished sounding. */
  playReference: () => Promise<void>
  /** One bar of clicks. Resolves when it has finished sounding. */
  playCountIn: () => Promise<void>
  /** Injected so tests need no fake timers, and so the sequence is assertable. */
  wait: (ms: number) => Promise<void>
  onPhase?: (phase: CapturePhase) => void
  /**
   * Every frame as it arrives, re-based, for the live input meter. The meter reads the same
   * frames the capture collects: a second stream for a meter would light a second microphone
   * indicator and could open a different device than the one being scored.
   */
  onFrame?: (frame: PitchFrame) => void
}

export interface CaptureWindowOptions {
  /** Room tone at the head of the window, for the noise floor. At least 500 ms (§5.1). */
  leadInMs: number
  /** Phrase duration plus a one-second tail (§6). */
  captureMs: number
}

export const runCaptureWindow = async (
  deps: CaptureWindowDeps,
  options: CaptureWindowOptions
): Promise<PitchFrame[]> => {
  const { setMuted, startFrames, playReference, playCountIn, wait, onPhase, onFrame } = deps
  const { leadInMs, captureMs } = options

  /**
   * Count-in first, then the reference — a teacher's "here it comes, [plays], sing", not an
   * ensemble's "1, 2, 3, 4, go". Anything that sounds *after* the reference sits between the
   * pitch the singer heard and the pitch they are about to produce, and interpolated tones erode
   * pitch memory. Swapping these two lines is the whole of the bug this order prevents.
   */
  onPhase?.('count-in')
  await playCountIn()
  onPhase?.('reference')
  await playReference()

  const frames: PitchFrame[] = []
  /**
   * The worklet timestamps frames from its own construction, not from this window. On the second
   * item of a session the first frame arrives tens of seconds in, and `noiseFloorRms` — which
   * selects `time < 0.5` — would find no lead-in at all.
   */
  let origin: number | null = null

  setMuted(true)
  try {
    const stopFrames = await startFrames(frame => {
      origin = origin ?? frame.time
      const rebased = { ...frame, time: frame.time - origin }
      frames.push(rebased)
      onFrame?.(rebased)
    })
    onPhase?.('lead-in')
    await wait(leadInMs)
    onPhase?.('listening')
    await wait(captureMs)
    stopFrames()
  } finally {
    setMuted(false)
  }

  onPhase?.('scoring')
  return frames
}
